import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Pause, 
  SkipForward, 
  SkipBack, 
  X, 
  CheckCircle, 
  Users, 
  Package, 
  TrendingUp, 
  Calendar,
  MessageSquare,
  FileText,
  Settings,
  Shield,
  Bell,
  Camera,
  Upload,
  Edit,
  Trash2,
  Save,
  Plus,
  Search,
  Filter,
  Download,
  Share2,
  Star,
  Clock,
  MapPin,
  Phone,
  Mail,
  Globe,
  Building,
  Monitor,
  Smartphone,
  Tablet,
  Award,
  Target,
  BarChart3,
  PieChart,
  Activity,
  Zap,
  Lock,
  Eye,
  Database,
  Cloud,
  Wifi,
  RefreshCw,
  ChevronRight,
  CheckCircle2,
  PlayCircle,
  Info,
  Palette,
  Sun,
  Moon,
  Sparkles
} from 'lucide-react';

const Demo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [progressAnimation, setProgressAnimation] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('blue');

  const themes = {
    blue: {
      name: 'Mavi Tema',
      primary: 'from-blue-500 to-blue-600',
      secondary: 'from-blue-50 to-blue-100',
      accent: 'from-blue-600 to-purple-600',
      gradient: 'from-blue-50 via-white to-purple-50',
      text: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: Sun
    },
    purple: {
      name: 'Mor Tema',
      primary: 'from-purple-500 to-purple-600',
      secondary: 'from-purple-50 to-purple-100',
      accent: 'from-purple-600 to-pink-600',
      gradient: 'from-purple-50 via-white to-pink-50',
      text: 'text-purple-600',
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: Sparkles
    },
    green: {
      name: 'Yeşil Tema',
      primary: 'from-green-500 to-green-600',
      secondary: 'from-green-50 to-green-100',
      accent: 'from-green-600 to-emerald-600',
      gradient: 'from-green-50 via-white to-emerald-50',
      text: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: Eye
    },
    dark: {
      name: 'Koyu Tema',
      primary: 'from-gray-700 to-gray-800',
      secondary: 'from-gray-800 to-gray-900',
      accent: 'from-gray-800 to-slate-800',
      gradient: 'from-gray-900 via-gray-800 to-slate-900',
      text: 'text-gray-300',
      bg: 'bg-gray-800',
      border: 'border-gray-700',
      icon: Moon
    }
  };

  const currentThemeConfig = themes[currentTheme];

  // Grid pattern component
  const AnimatedGridPattern = () => (
    <div className="absolute inset-0 overflow-hidden opacity-30">
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path 
              d="M 50 0 L 0 0 0 50" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="0.5"
              className={currentTheme === 'dark' ? 'text-gray-600' : 'text-gray-300'}
            />
          </pattern>
          <linearGradient id="gridGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="50%" stopColor={currentTheme === 'dark' ? '#374151' : '#e5e7eb'} stopOpacity="0.5" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
        <motion.rect 
          width="100%" 
          height="100%" 
          fill="url(#gridGradient)"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.8, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </svg>
      
      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className={`absolute w-2 h-2 rounded-full bg-gradient-to-r ${currentThemeConfig.primary} opacity-60`}
          initial={{ 
            x: Math.random() * window.innerWidth, 
            y: Math.random() * window.innerHeight 
          }}
          animate={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
          }}
          transition={{
            duration: 20 + Math.random() * 20,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "linear"
          }}
        />
      ))}
    </div>
  );

  const demoSteps = [
    {
      id: 1,
      title: "Hoş Geldiniz",
      subtitle: "İş Takip Otomasyonu Sistemi",
      description: "Modern ve kullanıcı dostu arayüzümüzle tanışın. Sistem, işletmenizin tüm süreçlerini kolayca yönetebilmenizi sağlar.",
      component: "welcome",
      duration: 4000,
      icon: Building,
      color: currentThemeConfig.accent
    },
    {
      id: 2,
      title: "Kullanıcı Profili",
      subtitle: "Kişisel Bilgiler ve Ayarlar",
      description: "Profil bilgilerinizi düzenleyin, fotoğraf ekleyin ve kişisel ayarlarınızı yapılandırın. Gelişmiş profil yönetimi özellikleri.",
      component: "profile",
      duration: 5000,
      icon: Users,
      color: "from-green-500 to-blue-500"
    },
    {
      id: 3,
      title: "Stok Yönetimi",
      subtitle: "Envanter Takibi",
      description: "Ürünlerinizi kategorize edin, stok seviyelerini takip edin ve otomatik uyarılar alın. Gelişmiş filtreleme ve raporlama.",
      component: "inventory",
      duration: 5000,
      icon: Package,
      color: "from-orange-500 to-red-500"
    },
    {
      id: 4,
      title: "Görev Yönetimi",
      subtitle: "Proje ve Görev Takibi",
      description: "Görevleri oluşturun, takım üyelerine atayın ve ilerlemeyi gerçek zamanlı takip edin. Kanban board ve zaman çizelgesi.",
      component: "tasks",
      duration: 5000,
      icon: Target,
      color: currentThemeConfig.accent
    },
    {
      id: 5,
      title: "Raporlama",
      subtitle: "Analitik ve İstatistikler",
      description: "Detaylı raporlar oluşturun, performans analizleri yapın ve karar verme süreçlerinizi iyileştirin. İnteraktif grafikler ve dashboard.",
      component: "reports",
      duration: 5000,
      icon: BarChart3,
      color: "from-teal-500 to-cyan-500"
    },
    {
      id: 6,
      title: "Güvenlik",
      subtitle: "Veri Güvenliği ve Gizlilik",
      description: "İki faktörlü kimlik doğrulama, rol bazlı yetkilendirme ve veri şifreleme ile güvenliğinizi sağlayın. KVKK uyumlu sistem.",
      component: "security",
      duration: 4000,
      icon: Shield,
      color: "from-emerald-500 to-green-600"
    }
  ];

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        if (currentStep < demoSteps.length - 1) {
          setProgressAnimation(true);
          setTimeout(() => {
            setCurrentStep(prev => prev + 1);
            setCompletedSteps(prev => [...prev, currentStep]);
            setProgressAnimation(false);
          }, 300);
        } else {
          setIsPlaying(false);
        }
      }, demoSteps[currentStep]?.duration || 4000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, demoSteps]);

  const handlePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    if (currentStep < demoSteps.length - 1) {
      setProgressAnimation(true);
      setTimeout(() => {
        setCompletedSteps(prev => [...prev, currentStep]);
        setCurrentStep(prev => prev + 1);
        setProgressAnimation(false);
      }, 300);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setProgressAnimation(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setCompletedSteps(prev => prev.filter(step => step !== currentStep - 1));
        setProgressAnimation(false);
      }, 300);
    }
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex !== currentStep) {
      setProgressAnimation(true);
      setTimeout(() => {
        setCurrentStep(stepIndex);
        setCompletedSteps(Array.from({ length: stepIndex }, (_, i) => i));
        setProgressAnimation(false);
      }, 300);
    }
  };

  const handleThemeChange = (themeName) => {
    setCurrentTheme(themeName);
  };

  const WelcomeDemo = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center space-y-8"
    >
      <div className="relative mx-auto w-40 h-40 mb-12">
        <div className={`absolute inset-0 bg-gradient-to-r ${currentThemeConfig.accent} rounded-full animate-spin`} style={{ animationDuration: '8s' }}></div>
        <div className={`absolute inset-2 ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-full flex items-center justify-center shadow-2xl`}>
          <Building className={`w-20 h-20 ${currentThemeConfig.text}`} />
        </div>
        <div className={`absolute -top-4 -right-4 bg-gradient-to-r ${currentThemeConfig.primary} rounded-full p-3 animate-bounce`}>
          <Zap className="w-6 h-6 text-white" />
        </div>
      </div>
      
      <div className="space-y-6">
        <motion.h1 
          className={`text-5xl md:text-6xl font-bold bg-gradient-to-r ${currentThemeConfig.accent} bg-clip-text text-transparent`}
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          İş Takip Otomasyonu
        </motion.h1>
        
        <motion.p 
          className={`text-2xl ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'} max-w-3xl mx-auto leading-relaxed`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          İşletmenizin dijital dönüşümünü başlatın. Modern teknoloji ile buluşan kullanıcı dostu tasarım.
        </motion.p>

        <motion.div 
          className={`inline-flex items-center space-x-2 ${currentThemeConfig.bg} px-6 py-3 rounded-full border ${currentThemeConfig.border}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <PlayCircle className={`w-5 h-5 ${currentThemeConfig.text}`} />
          <span className={`${currentThemeConfig.text} font-medium`}>İnteraktif demo deneyimi</span>
        </motion.div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-4xl mx-auto">
        {[
          { icon: Users, label: "Takım Yönetimi", desc: "Ekip koordinasyonu", color: "blue" },
          { icon: Package, label: "Stok Takibi", desc: "Envanter kontrolü", color: "orange" },
          { icon: TrendingUp, label: "Analitik", desc: "Performans raporları", color: "green" },
          { icon: Shield, label: "Güvenlik", desc: "Veri koruması", color: "purple" }
        ].map(({ icon: Icon, label, desc, color }, index) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.8 + index * 0.1 }}
            className={`p-6 ${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white hover:border-gray-300'} rounded-2xl shadow-lg border-2 border-transparent transition-all duration-300 group hover:scale-105`}
          >
            <div className={`w-12 h-12 bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <h3 className={`font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>{label}</h3>
            <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{desc}</p>
          </motion.div>
        ))}
      </div>

      <motion.div 
        className="flex flex-wrap justify-center gap-4 mt-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <div className={`flex items-center space-x-2 ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} px-4 py-2 rounded-full`}>
          <Monitor className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
          <span className={`text-sm ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Web Tabanlı</span>
        </div>
        <div className={`flex items-center space-x-2 ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} px-4 py-2 rounded-full`}>
          <Smartphone className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
          <span className={`text-sm ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Mobil Uyumlu</span>
        </div>
        <div className={`flex items-center space-x-2 ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} px-4 py-2 rounded-full`}>
          <Cloud className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
          <span className={`text-sm ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Bulut Tabanlı</span>
        </div>
      </motion.div>
    </motion.div>
  );

  const ProfileDemo = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="max-w-6xl mx-auto"
    >
      <div className={`bg-gradient-to-br ${currentTheme === 'dark' ? 'from-gray-800 to-gray-900' : 'from-white to-gray-50'} rounded-3xl shadow-2xl p-8 border ${currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-6 lg:space-y-0 lg:space-x-8 mb-10">
          <div className="relative group">
            <div className={`w-32 h-32 bg-gradient-to-br ${currentThemeConfig.accent} rounded-3xl flex items-center justify-center shadow-2xl`}>
              <Users className="w-16 h-16 text-white" />
            </div>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.5, type: "spring" }}
              className="absolute -bottom-3 -right-3 bg-gradient-to-r from-green-400 to-green-600 rounded-full p-3 shadow-lg"
            >
              <Camera className="w-5 h-5 text-white" />
            </motion.div>
            <div className={`absolute inset-0 bg-gradient-to-br ${currentThemeConfig.primary}/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity`} />
          </div>
          
          <div className="flex-1 space-y-4">
            <div>
              <h2 className={`text-3xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>Ahmet Yılmaz</h2>
              <div className="flex items-center space-x-3">
                <span className={`${currentThemeConfig.bg} ${currentThemeConfig.text} px-3 py-1 rounded-full text-sm font-medium`}>Proje Yöneticisi</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Aktif</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`flex items-center space-x-3 p-3 ${currentThemeConfig.bg} rounded-xl`}>
                <Mail className={`w-5 h-5 ${currentThemeConfig.text}`} />
                <div>
                  <p className={`text-xs ${currentThemeConfig.text} uppercase tracking-wide`}>Email</p>
                  <p className={`font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>ahmet@company.com</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl">
                <Phone className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-xs text-green-600 uppercase tracking-wide">Telefon</p>
                  <p className={`font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>+90 555 123 4567</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col space-y-3">
            <button className={`flex items-center space-x-2 bg-gradient-to-r ${currentThemeConfig.accent} text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all`}>
              <Edit className="w-4 h-4" />
              <span>Profili Düzenle</span>
            </button>
            <button className={`flex items-center space-x-2 border ${currentTheme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} px-6 py-3 rounded-xl transition-colors`}>
              <Settings className="w-4 h-4" />
              <span>Ayarlar</span>
            </button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Tamamlanan Proje", value: "47", icon: CheckCircle2, color: "green" },
            { label: "Aktif Görevler", value: "12", icon: Activity, color: "blue" },
            { label: "Takım Üyeleri", value: "8", icon: Users, color: "purple" },
            { label: "Başarı Oranı", value: "94%", icon: Award, color: "orange" }
          ].map(({ label, value, icon: Icon, color }, index) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className={`p-4 ${currentTheme === 'dark' ? `bg-gray-700 border-gray-600` : `bg-gradient-to-br from-${color}-50 to-${color}-100 border-${color}-200`} border rounded-2xl`}
            >
              <Icon className={`w-6 h-6 ${currentTheme === 'dark' ? `text-${color}-400` : `text-${color}-600`} mb-2`} />
              <p className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{value}</p>
              <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{label}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Personal Info */}
          <div className="space-y-6">
            <h3 className={`text-xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center space-x-2`}>
              <Info className={`w-5 h-5 ${currentThemeConfig.text}`} />
              <span>Kişisel Bilgiler</span>
            </h3>
            
            <div className="space-y-4">
              {[
                { label: "Ad Soyad", value: "Ahmet Yılmaz", icon: Users, color: "blue" },
                { label: "Pozisyon", value: "Proje Yöneticisi", icon: Building, color: "purple" },
                { label: "Lokasyon", value: "İstanbul, Türkiye", icon: MapPin, color: "green" },
                { label: "Deneyim", value: "5+ Yıl", icon: Star, color: "orange" },
                { label: "Katılım Tarihi", value: "15 Ocak 2022", icon: Calendar, color: "teal" },
                { label: "Son Aktivite", value: "2 saat önce", icon: Clock, color: "red" }
              ].map(({ label, value, icon: Icon, color }, index) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className={`flex items-center p-4 ${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-gray-100 hover:shadow-md'} rounded-xl shadow-sm border transition-all group`}
                >
                  <div className={`w-10 h-10 bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{label}</p>
                    <p className={`font-semibold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{value}</p>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-gray-500 group-hover:text-gray-400' : 'text-gray-400 group-hover:text-gray-600'} transition-colors`} />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Skills & Achievements */}
          <div className="space-y-6">
            <h3 className={`text-xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center space-x-2`}>
              <Award className="w-5 h-5 text-purple-600" />
              <span>Yetenekler & Başarılar</span>
            </h3>
            
            <div className="space-y-4">
              {[
                { skill: "Proje Yönetimi", level: 95, color: "blue" },
                { skill: "Takım Liderliği", level: 88, color: "green" },
                { skill: "Stratejik Planlama", level: 92, color: "purple" },
                { skill: "İletişim", level: 90, color: "orange" },
                { skill: "Problem Çözme", level: 87, color: "teal" },
                { skill: "Teknoloji", level: 82, color: "red" }
              ].map(({ skill, level, color }, index) => (
                <motion.div
                  key={skill}
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "100%" }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="space-y-2"
                >
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{skill}</span>
                    <span className={`text-sm font-bold ${currentTheme === 'dark' ? 'text-white bg-gray-700' : 'text-gray-900 bg-gray-100'} px-2 py-1 rounded`}>{level}%</span>
                  </div>
                  <div className={`h-3 ${currentTheme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${level}%` }}
                      transition={{ delay: 0.6 + index * 0.1, duration: 1.5, ease: "easeOut" }}
                      className={`h-full bg-gradient-to-r from-${color}-400 to-${color}-600 rounded-full relative`}
                    >
                      <div className="absolute inset-0 bg-white/20 animate-pulse" />
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              {[
                { icon: Award, label: "Sertifikalar", count: "12", color: "yellow" },
                { icon: Target, label: "Hedefler", count: "8/10", color: "red" }
              ].map(({ icon: Icon, label, count, color }, index) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className={`p-4 ${currentTheme === 'dark' ? `bg-gray-700 border-gray-600` : `bg-gradient-to-br from-${color}-50 to-${color}-100 border-${color}-200`} border rounded-xl text-center`}
                >
                  <Icon className={`w-6 h-6 ${currentTheme === 'dark' ? `text-${color}-400` : `text-${color}-600`} mx-auto mb-2`} />
                  <p className={`font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{count}</p>
                  <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const InventoryDemo = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto"
    >
      <div className={`${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl shadow-lg p-8`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Stok Yönetimi</h2>
            <p className={`${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Envanter takibi ve stok kontrolleri</p>
          </div>
          <div className="flex space-x-2">
            <button className={`flex items-center px-4 py-2 bg-gradient-to-r ${currentThemeConfig.accent} text-white rounded-lg hover:shadow-lg transition-all`}>
              <Plus className="w-4 h-4 mr-2" />
              Yeni Ürün
            </button>
            <button className={`flex items-center px-4 py-2 border ${currentTheme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'} rounded-lg transition-colors`}>
              <Download className="w-4 h-4 mr-2" />
              Rapor
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { title: "Toplam Ürün", value: "1,247", change: "+5.2%", color: "blue" },
            { title: "Stok Değeri", value: "₺847K", change: "+12.5%", color: "green" },
            { title: "Düşük Stok", value: "23", change: "-8.1%", color: "orange" },
            { title: "Kritik Stok", value: "5", change: "+2", color: "red" }
          ].map(({ title, value, change, color }, index) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`${currentTheme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-lg p-6`}
            >
              <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{title}</p>
              <p className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>{value}</p>
              <p className={`text-sm ${
                color === 'green' ? 'text-green-500' :
                color === 'red' ? 'text-red-500' :
                color === 'orange' ? 'text-orange-500' :
                'text-blue-500'
              }`}>
                {change}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className={`text-lg font-semibold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Son Eklenen Ürünler</h3>
            <div className="flex space-x-2">
              <button className={`p-2 border ${currentTheme === 'dark' ? 'border-gray-600 hover:bg-gray-700 text-gray-400' : 'border-gray-300 hover:bg-gray-50 text-gray-600'} rounded-lg transition-colors`}>
                <Search className="w-4 h-4" />
              </button>
              <button className={`p-2 border ${currentTheme === 'dark' ? 'border-gray-600 hover:bg-gray-700 text-gray-400' : 'border-gray-300 hover:bg-gray-50 text-gray-600'} rounded-lg transition-colors`}>
                <Filter className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {[
              { name: "MacBook Pro 16\"", category: "Elektronik", stock: 15, status: "Yeterli" },
              { name: "Ofis Sandalyesi", category: "Mobilya", stock: 8, status: "Düşük" },
              { name: "Yazıcı Kartuşu", category: "Toner", stock: 3, status: "Kritik" },
              { name: "Projeksiyon Cihazı", category: "Elektronik", stock: 25, status: "Yeterli" }
            ].map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center justify-between p-4 border ${currentTheme === 'dark' ? 'border-gray-700 hover:bg-gray-700 bg-gray-800' : 'border-gray-200 hover:bg-gray-50 bg-white'} rounded-lg transition-colors`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${currentThemeConfig.accent} rounded-lg flex items-center justify-center`}>
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className={`font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{product.name}</h4>
                    <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{product.category}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <p className={`font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{product.stock} Adet</p>
                    <p className={`text-sm ${
                      product.status === 'Yeterli' ? 'text-green-500' :
                      product.status === 'Düşük' ? 'text-orange-500' :
                      'text-red-500'
                    }`}>
                      {product.status}
                    </p>
                  </div>
                  <div className="flex space-x-1">
                    <button className={`p-2 ${currentTheme === 'dark' ? 'text-gray-400 hover:text-blue-400' : 'text-gray-400 hover:text-blue-600'} transition-colors`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className={`p-2 ${currentTheme === 'dark' ? 'text-gray-400 hover:text-red-400' : 'text-gray-400 hover:text-red-600'} transition-colors`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const TasksDemo = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto"
    >
      <div className={`${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} border rounded-xl shadow-lg p-8`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Görev Yönetimi</h2>
            <p className={`${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Proje takibi ve görev atamaları</p>
          </div>
          <div className="flex space-x-2">
            <button className={`flex items-center px-4 py-2 bg-gradient-to-r ${currentThemeConfig.accent} text-white rounded-lg hover:shadow-lg transition-all`}>
              <Plus className="w-4 h-4 mr-2" />
              Yeni Görev
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Yapılacak", count: 8, color: "gray", tasks: ["Website Tasarımı", "Logo Düzenlemesi", "İçerik Yazımı"] },
            { title: "Devam Eden", count: 5, color: "blue", tasks: ["Mobil Uygulama", "API Geliştirme", "Test Süreçleri"] },
            { title: "Tamamlanan", count: 12, color: "green", tasks: ["Veritabanı Kurulumu", "Güvenlik Testleri", "Deployment"] }
          ].map((column, columnIndex) => (
            <motion.div
              key={column.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: columnIndex * 0.2 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{column.title}</h3>
                <span className={`px-2 py-1 text-sm rounded-full ${
                  column.color === 'gray' ? `${currentTheme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}` :
                  column.color === 'blue' ? `${currentTheme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}` :
                  `${currentTheme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'}`
                }`}>
                  {column.count}
                </span>
              </div>

              <div className="space-y-3">
                {column.tasks.map((task, taskIndex) => (
                  <motion.div
                    key={task}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: columnIndex * 0.2 + taskIndex * 0.1 }}
                    className={`p-4 border ${currentTheme === 'dark' ? 'border-gray-700 hover:bg-gray-700 bg-gray-800' : 'border-gray-200 hover:shadow-md bg-white'} rounded-lg transition-all`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className={`font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{task}</h4>
                      <div className="flex items-center space-x-1">
                        <div className={`w-6 h-6 bg-gradient-to-r ${currentThemeConfig.accent} rounded-full flex items-center justify-center`}>
                          <span className="text-xs font-medium text-white">A</span>
                        </div>
                      </div>
                    </div>
                    <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                      {column.color === 'green' ? 'Tamamlandı' : 'Devam ediyor...'}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Clock className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>2 gün</span>
                      </div>
                      <div className={`w-3 h-3 rounded-full ${
                        column.color === 'gray' ? `${currentTheme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'}` :
                        column.color === 'blue' ? 'bg-blue-500' :
                        'bg-green-500'
                      }`} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );

  const ReportsDemo = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto"
    >
      <div className={`${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} border rounded-xl shadow-lg p-8`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Raporlama & Analitik</h2>
            <p className={`${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Performans analizi ve detaylı raporlar</p>
          </div>
          <div className="flex space-x-2">
            <button className={`flex items-center px-4 py-2 border ${currentTheme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'} rounded-lg transition-colors`}>
              <Download className="w-4 h-4 mr-2" />
              Excel
            </button>
            <button className={`flex items-center px-4 py-2 border ${currentTheme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'} rounded-lg transition-colors`}>
              <Share2 className="w-4 h-4 mr-2" />
              Paylaş
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { title: "Toplam Satış", value: "₺2.4M", change: "+15.3%", icon: TrendingUp },
            { title: "Aktif Projeler", value: "47", change: "+8.2%", icon: FileText },
            { title: "Takım Üyeleri", value: "23", change: "+2", icon: Users },
            { title: "Tamamlanan Görevler", value: "156", change: "+22.1%", icon: CheckCircle }
          ].map(({ title, value, change, icon: Icon }, index) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`${currentTheme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'} border rounded-lg p-6`}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon className={`w-8 h-8 ${currentThemeConfig.text}`} />
                <span className="text-sm text-green-500 font-medium">{change}</span>
              </div>
              <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{title}</p>
              <p className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{value}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <h3 className={`text-lg font-semibold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Aylık Performans</h3>
            <div className={`h-64 ${currentTheme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4 flex items-center justify-center`}>
              <div className="text-center">
                <TrendingUp className={`w-16 h-16 ${currentThemeConfig.text} mx-auto mb-4`} />
                <p className={`${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Grafik Görünümü</p>
                <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>İnteraktif grafik burada gösterilir</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-4"
          >
            <h3 className={`text-lg font-semibold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>En Çok Kullanılan Modüller</h3>
            <div className="space-y-3">
              {[
                { name: "Stok Yönetimi", usage: 89, color: "blue" },
                { name: "Görev Takibi", usage: 76, color: "green" },
                { name: "Kullanıcı Profilleri", usage: 68, color: "purple" },
                { name: "Raporlama", usage: 45, color: "orange" }
              ].map(({ name, usage, color }, index) => (
                <motion.div
                  key={name}
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "100%" }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="space-y-2"
                >
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{name}</span>
                    <span className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{usage}%</span>
                  </div>
                  <div className={`h-2 ${currentTheme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${usage}%` }}
                      transition={{ delay: 0.6 + index * 0.1, duration: 1 }}
                      className={`h-full rounded-full ${
                        color === 'blue' ? 'bg-blue-500' :
                        color === 'green' ? 'bg-green-500' :
                        color === 'purple' ? 'bg-purple-500' :
                        'bg-orange-500'
                      }`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );

  const SecurityDemo = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className={`${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} border rounded-xl shadow-lg p-8`}>
        <div className="text-center mb-8">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className={`absolute inset-0 bg-gradient-to-r ${currentThemeConfig.primary} rounded-full animate-pulse`}></div>
            <div className={`absolute inset-2 ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-full flex items-center justify-center`}>
              <Shield className="w-12 h-12 text-green-500" />
            </div>
          </div>
          <h2 className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>Güvenlik & Gizlilik</h2>
          <p className={`${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Verileriniz tamamen güvende</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h3 className={`text-lg font-semibold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Güvenlik Özellikleri</h3>
            {[
              { title: "2FA Kimlik Doğrulama", status: "Aktif", icon: Shield },
              { title: "SSL Şifreleme", status: "Aktif", icon: Globe },
              { title: "Otomatik Yedekleme", status: "Günlük", icon: Save },
              { title: "Erişim Logları", status: "Monitörleniyor", icon: Clock }
            ].map(({ title, status, icon: Icon }, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center p-4 ${currentTheme === 'dark' ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'} rounded-lg border`}
              >
                <Icon className="w-6 h-6 text-green-500 mr-4" />
                <div className="flex-1">
                  <p className={`font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{title}</p>
                  <p className="text-sm text-green-500">{status}</p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </motion.div>
            ))}
          </div>

          <div className="space-y-6">
            <h3 className={`text-lg font-semibold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Gizlilik Ayarları</h3>
            {[
              { title: "Profil Görünürlüğü", status: "Takım" },
              { title: "Veri Paylaşımı", status: "Kapalı" },
              { title: "Bildirim Tercihleri", status: "Özelleştirilmiş" },
              { title: "Çerez Ayarları", status: "Yalnızca Gerekli" }
            ].map(({ title, status }, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center justify-between p-4 border ${currentTheme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} rounded-lg`}
              >
                <div>
                  <p className={`font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{title}</p>
                  <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{status}</p>
                </div>
                <Settings className={`w-5 h-5 ${currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className={`mt-8 p-6 ${currentTheme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'} rounded-lg border`}
        >
          <div className="flex items-center mb-4">
            <Bell className={`w-6 h-6 ${currentTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'} mr-3`} />
            <h4 className={`font-semibold ${currentTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>Güvenlik Durumu</h4>
          </div>
          <p className={`${currentTheme === 'dark' ? 'text-gray-300' : 'text-blue-700'} mb-4`}>
            Sisteminiz tamamen güvenli. Son güvenlik taraması 2 saat önce tamamlandı.
          </p>
          <div className="flex space-x-4">
            <button className={`px-4 py-2 bg-gradient-to-r ${currentThemeConfig.accent} text-white rounded-lg hover:shadow-lg transition-all`}>
              Detaylı Rapor
            </button>
            <button className={`px-4 py-2 border ${currentTheme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-blue-300 text-blue-700 hover:bg-blue-50'} rounded-lg transition-colors`}>
              Ayarları Düzenle
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  const renderCurrentStep = () => {
    const currentStepData = demoSteps[currentStep];
    if (!currentStepData) return null;

    switch (currentStepData.component) {
      case 'welcome':
        return <WelcomeDemo />;
      case 'profile':
        return <ProfileDemo />;
      case 'inventory':
        return <InventoryDemo />;
      case 'tasks':
        return <TasksDemo />;
      case 'reports':
        return <ReportsDemo />;
      case 'security':
        return <SecurityDemo />;
      default:
        return <WelcomeDemo />;
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${currentThemeConfig.gradient} relative`}>
      {/* Animated Grid Background */}
      <AnimatedGridPattern />
      
      {/* Enhanced Header */}
      <div className={`sticky top-0 z-50 ${currentTheme === 'dark' ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-xl border-b ${currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-200'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-6">
              <div className={`w-12 h-12 bg-gradient-to-r ${currentThemeConfig.accent} rounded-2xl flex items-center justify-center shadow-lg`}>
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Demo Gezisi</h1>
                <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>İnteraktif sistem tanıtımı</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Theme Selector */}
              <div className="flex items-center space-x-2">
                <Palette className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                <div className="flex space-x-1">
                  {Object.entries(themes).map(([key, theme]) => {
                    const IconComponent = theme.icon;
                    return (
                      <motion.button
                        key={key}
                        onClick={() => handleThemeChange(key)}
                        className={`p-2 rounded-lg transition-all ${
                          currentTheme === key 
                            ? `bg-gradient-to-r ${theme.accent} text-white shadow-lg` 
                            : `${currentTheme === 'dark' ? 'bg-gray-800 hover:bg-gray-700 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title={theme.name}
                      >
                        <IconComponent className="w-4 h-4" />
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              <div className={`hidden md:flex items-center space-x-2 ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} px-4 py-2 rounded-full`}>
                <Eye className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                <span className={`text-sm ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Canlı Demo</span>
              </div>
              <button
                onClick={() => window.history.back()}
                className={`p-3 ${currentTheme === 'dark' ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'} rounded-xl transition-colors`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Progress Section */}
      <div className={`${currentTheme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border-b shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Step Info */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className={`w-14 h-14 bg-gradient-to-r ${demoSteps[currentStep]?.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                {React.createElement(demoSteps[currentStep]?.icon, { className: "w-7 h-7 text-white" })}
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {demoSteps[currentStep]?.title}
                </h2>
                <p className={`${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{demoSteps[currentStep]?.subtitle}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className={`text-sm ${currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Adım</p>
                <p className={`text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {currentStep + 1}<span className={`${currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>/{demoSteps.length}</span>
                </p>
              </div>
              {isPlaying && (
                <div className="flex items-center space-x-2 bg-green-100 px-3 py-2 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm text-green-700 font-medium">Otomatik oynatılıyor</span>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Progress Bar */}
          <div className="space-y-4">
            <div className="relative">
              <div className={`w-full ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'} rounded-full h-4 shadow-inner`}>
                <motion.div
                  className={`bg-gradient-to-r ${currentThemeConfig.accent} h-4 rounded-full relative overflow-hidden shadow-lg`}
                  initial={{ width: 0 }}
                  animate={{ 
                    width: `${((currentStep + 1) / demoSteps.length) * 100}%`,
                    scale: progressAnimation ? [1, 1.05, 1] : 1
                  }}
                  transition={{ 
                    width: { duration: 0.8, ease: "easeInOut" },
                    scale: { duration: 0.3 }
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                </motion.div>
              </div>
              
              {/* Progress Percentage */}
              <div className="absolute top-0 right-0 -mt-8">
                <div className={`${currentTheme === 'dark' ? 'bg-white text-gray-900' : 'bg-gray-900 text-white'} px-3 py-1 rounded-lg text-sm font-medium`}>
                  {Math.round(((currentStep + 1) / demoSteps.length) * 100)}%
                </div>
              </div>
            </div>

            {/* Step Indicators */}
            <div className="flex justify-between items-center">
              {demoSteps.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(index)}
                  className="group relative flex flex-col items-center space-y-2"
                >
                  <motion.div
                    className={`w-6 h-6 rounded-full transition-all duration-300 ${
                      index <= currentStep
                        ? `bg-gradient-to-r ${step.color} shadow-lg`
                        : `${currentTheme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-300 hover:bg-gray-400'}`
                    }`}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {index < currentStep && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={`w-full h-full ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-full flex items-center justify-center`}
                      >
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </motion.div>
                    )}
                    {index === currentStep && (
                      <motion.div
                        className="w-full h-full rounded-full flex items-center justify-center"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {React.createElement(step.icon, { className: "w-3 h-3 text-white" })}
                      </motion.div>
                    )}
                  </motion.div>
                  
                  {/* Step Label */}
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className={`${currentTheme === 'dark' ? 'bg-white text-gray-900' : 'bg-gray-900 text-white'} px-2 py-1 rounded text-xs whitespace-nowrap`}>
                      {step.title}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="text-center mb-12">
          <motion.p 
            className={`text-xl ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'} max-w-4xl mx-auto leading-relaxed`}
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {demoSteps[currentStep]?.description}
          </motion.p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.95 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            {renderCurrentStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Enhanced Controls */}
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
        <motion.div 
          className={`${currentTheme === 'dark' ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-xl rounded-2xl shadow-2xl border ${currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-200'} px-8 py-4`}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center space-x-6">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`p-3 ${currentTheme === 'dark' ? 'text-gray-400 hover:text-gray-300 disabled:opacity-50 hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 disabled:opacity-50 hover:bg-gray-100'} disabled:cursor-not-allowed rounded-xl transition-all`}
            >
              <SkipBack className="w-6 h-6" />
            </button>
            
            <motion.button
              onClick={handlePlay}
              className={`p-4 bg-gradient-to-r ${currentThemeConfig.accent} text-white rounded-2xl hover:shadow-lg transition-all`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ rotate: isPlaying ? 360 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
              </motion.div>
            </motion.button>
            
            <button
              onClick={handleNext}
              disabled={currentStep === demoSteps.length - 1}
              className={`p-3 ${currentTheme === 'dark' ? 'text-gray-400 hover:text-gray-300 disabled:opacity-50 hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 disabled:opacity-50 hover:bg-gray-100'} disabled:cursor-not-allowed rounded-xl transition-all`}
            >
              <SkipForward className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress Timer */}
          {isPlaying && (
            <motion.div 
              className="mt-3 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="flex items-center justify-center space-x-2">
                <RefreshCw className={`w-4 h-4 ${currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-500'} animate-spin`} />
                <span className={`text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Sonraki adım: {Math.ceil(demoSteps[currentStep]?.duration / 1000)}s</span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Demo; 